import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, Mic, MicOff } from 'lucide-react';
import { useExpertChat } from '../../contexts/ExpertChatContext';
import { useVoiceInput } from '../../hooks/useVoiceInput';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  disabled = false,
  placeholder = 'Ask the Expert...',
}) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { voiceInputEnabled, autoSendVoice } = useExpertChat();

  const {
    startRecording,
    stopRecording,
    isRecording,
    isTranscribing,
    permissionState,
    transcript,
    clearTranscript,
  } = useVoiceInput();

  // Track whether the Space key is currently held for push-to-talk
  const spaceHeldRef = useRef(false);
  // Track whether the mic button is being pressed
  const micPressedRef = useRef(false);

  // ─── Auto-focus ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (!disabled && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [disabled]);

  // ─── Inject transcript into the message field ─────────────────────────────

  useEffect(() => {
    if (!transcript) return;

    // Only populate once transcription is complete (not still recording via WebSpeech)
    if (isRecording) {
      // Show live interim text while recording
      setMessage(transcript);
    } else {
      // Final transcript available — populate and optionally auto-send
      setMessage(transcript);
      if (autoSendVoice && transcript.trim()) {
        // Defer slightly so React flushes the state update first
        setTimeout(() => {
          onSend(transcript.trim());
          setMessage('');
          clearTranscript();
        }, 50);
      }
    }
  }, [transcript, isRecording, autoSendVoice, onSend, clearTranscript]);

  // ─── Space key push-to-talk (global, only when textarea is not focused) ───

  const hotkey = (() => {
    try {
      return localStorage.getItem('voice_hotkey') || 'Space';
    } catch {
      return 'Space';
    }
  })();

  const handleGlobalKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!voiceInputEnabled) return;
      if (document.activeElement === textareaRef.current) return;
      if (isTranscribing) return;

      const matchedKey =
        (hotkey === 'Space' && e.code === 'Space') ||
        (hotkey === 'V' && e.code === 'KeyV');

      if (!matchedKey) return;
      if (spaceHeldRef.current) return; // already held

      e.preventDefault();
      spaceHeldRef.current = true;
      startRecording();
    },
    [voiceInputEnabled, isTranscribing, hotkey, startRecording]
  );

  const handleGlobalKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (!voiceInputEnabled) return;

      const matchedKey =
        (hotkey === 'Space' && e.code === 'Space') ||
        (hotkey === 'V' && e.code === 'KeyV');

      if (!matchedKey) return;
      if (!spaceHeldRef.current) return;

      spaceHeldRef.current = false;
      stopRecording();
    },
    [voiceInputEnabled, hotkey, stopRecording]
  );

  useEffect(() => {
    if (!voiceInputEnabled) return;
    window.addEventListener('keydown', handleGlobalKeyDown);
    window.addEventListener('keyup', handleGlobalKeyUp);
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
      window.removeEventListener('keyup', handleGlobalKeyUp);
    };
  }, [voiceInputEnabled, handleGlobalKeyDown, handleGlobalKeyUp]);

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = () => {
    const trimmed = message.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setMessage('');
    clearTranscript();
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInput = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  // ─── Mic button handlers (mouse + touch for push-to-talk) ─────────────────

  const handleMicPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    if (isTranscribing) return;
    micPressedRef.current = true;
    startRecording();
  };

  const handleMicPointerUp = () => {
    if (!micPressedRef.current) return;
    micPressedRef.current = false;
    stopRecording();
  };

  // Also stop if pointer leaves the button while held
  const handleMicPointerLeave = () => {
    if (micPressedRef.current && isRecording) {
      micPressedRef.current = false;
      stopRecording();
    }
  };

  // ─── Derived state for UI ─────────────────────────────────────────────────

  const micDisabled = disabled || permissionState === 'denied';

  const micTitle =
    permissionState === 'denied'
      ? 'Microphone access denied'
      : isRecording
      ? 'Release to stop'
      : `Hold to speak (${hotkey === 'Space' ? 'or hold Space when not typing' : `or hold ${hotkey}`})`;

  return (
    <div className="border-t border-slate-700 p-3 bg-slate-900">
      {disabled && (
        <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
          <Loader2 size={12} className="animate-spin" />
          <span>Expert is thinking...</span>
        </div>
      )}

      {/* Voice recording status banner */}
      {voiceInputEnabled && isRecording && (
        <div className="flex items-center gap-2 text-xs text-red-400 mb-2 animate-pulse">
          <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
          <span>Listening...</span>
        </div>
      )}
      {voiceInputEnabled && isTranscribing && (
        <div className="flex items-center gap-2 text-xs text-teal-400 mb-2">
          <Loader2 size={12} className="animate-spin" />
          <span>Transcribing...</span>
        </div>
      )}

      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          placeholder={
            voiceInputEnabled && isRecording
              ? 'Listening...'
              : placeholder
          }
          disabled={disabled}
          rows={1}
          className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 resize-none focus:outline-none focus:border-teal-500 transition-colors disabled:opacity-50"
        />

        {/* Mic button — visible only when voice input is enabled */}
        {voiceInputEnabled && (
          <button
            onPointerDown={handleMicPointerDown}
            onPointerUp={handleMicPointerUp}
            onPointerLeave={handleMicPointerLeave}
            disabled={micDisabled}
            title={micTitle}
            className={`p-2 rounded-lg transition-colors select-none touch-none ${
              isRecording
                ? 'bg-red-500 hover:bg-red-400 text-white animate-pulse'
                : isTranscribing
                ? 'bg-slate-700 text-teal-400 cursor-wait'
                : permissionState === 'denied'
                ? 'bg-slate-700 text-slate-600 cursor-not-allowed'
                : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
            }`}
          >
            {isTranscribing ? (
              <Loader2 size={18} className="animate-spin" />
            ) : isRecording ? (
              <MicOff size={18} />
            ) : (
              <Mic size={18} />
            )}
          </button>
        )}

        <button
          onClick={handleSubmit}
          disabled={disabled || !message.trim()}
          className="p-2 bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 rounded-lg transition-colors"
        >
          {disabled ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Send size={18} />
          )}
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
